import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiDownload } from "react-icons/fi";
import { getAuditTrail, filterAudit, AUDIT_RESOURCE_TYPES } from "../../services/settingsApi";
import { downloadCsv } from "../../utils/exportCsv";
import { dayStartIso, dayEndIso } from "../../services/core";
import { Card, PageHeader, DataTable, SearchInput, Button, ErrorState, FormField, MultiSelect } from "../../components/common";
import { formatDateTime } from "../../utils/format";
import { getErrorMessage } from "../../utils/errors";

const api = { getAuditTrail };
const PAGE = 200;
// [UPDATED] PM-18: record type and outcome are checkbox filters. One value is filtered by the server;
// several values load all entries of the period and are filtered here (OR inside a filter, AND between filters).
const OUTCOMES = [{ value: "SUCCESS", label: "Success" }, { value: "DENIED", label: "Denied" }, { value: "FAILURE", label: "Failure" }];
const one = (list) => (list.length === 1 ? list[0] : "");

export default function AuditTrailPage() {
  const [search, setSearch] = useState("");
  const [server, setServer] = useState({ entityTypes: [], outcomes: [], from: "", to: "" });
  const [rows, setRows] = useState([]);
  const [state, setState] = useState({ loading: true, error: null, more: false });
  const typeParam = one(server.entityTypes);
  const outcomeParam = one(server.outcomes);

  const load = async (offset = 0) => {
    if (server.from && server.to && server.to < server.from) { toast.error("The 'to' date must be on or after the 'from' date."); return; }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.getAuditTrail({
        entityType: typeParam,
        outcome: outcomeParam,
        since: server.from ? dayStartIso(server.from) : undefined,
        until: server.to ? dayEndIso(server.to) : undefined,
        limit: PAGE,
        offset,
      });
      setRows((prev) => (offset ? [...prev, ...data] : data));
      setState({ loading: false, error: null, more: data.length === PAGE });
    } catch (err) {
      setState({ loading: false, error: getErrorMessage(err), more: false });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(0); }, [typeParam, outcomeParam, server.from, server.to]);

  const shown = useMemo(() => filterAudit(rows, search).filter((r) =>
    (!server.entityTypes.length || server.entityTypes.includes(r.resourceType))
    && (!server.outcomes.length || server.outcomes.includes(r.outcome))), [rows, search, server.entityTypes, server.outcomes]);

  if (state.error && !rows.length) return <ErrorState message={state.error} onRetry={() => load(0)} />;

  const columns = [
    { key: "at", label: "When", render: (r) => formatDateTime(r.at), value: (r) => formatDateTime(r.at) },
    { key: "entityType", label: "Record" },
    { key: "action", label: "Action", render: (r) => <span className="cell-stack"><strong>{r.action}</strong>{r.outcome !== "SUCCESS" && <small>{r.outcome}</small>}</span> },
    { key: "employeeName", label: "Employee" },
    { key: "actorName", label: "Done by" },
    { key: "comment", label: "Details", render: (r) => r.comment || "—" },
    { key: "changes", label: "What changed (before → after)", render: (r) => r.changes || "—" },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Audit trail"
        subtitle="Every approval, finalization and configuration change, newest first."
        actions={<Button variant="ghost" icon={FiDownload} onClick={() => downloadCsv("audit-trail", columns, shown)}>Export</Button>}
      />
      <div className="filter-bar">
        <SearchInput value={search} onChange={setSearch} placeholder="Search loaded entries (person, action, detail)" />
        <MultiSelect label="Record" allLabel="All records" options={AUDIT_RESOURCE_TYPES} value={server.entityTypes} onChange={(v) => setServer({ ...server, entityTypes: v })} />
        <MultiSelect label="Outcome" allLabel="Any outcome" keepOrder options={OUTCOMES} value={server.outcomes} onChange={(v) => setServer({ ...server, outcomes: v })} />
        <FormField label="From">
          <input type="date" value={server.from} onChange={(e) => setServer({ ...server, from: e.target.value })} />
        </FormField>
        <FormField label="To">
          <input type="date" value={server.to} onChange={(e) => setServer({ ...server, to: e.target.value })} />
        </FormField>
      </div>
      <p className="muted">
        {rows.length} entries loaded{state.more ? " (older entries available)" : ""}. Search looks through the loaded entries.
      </p>
      <Card padded={false}>
        <DataTable keepOrder loading={state.loading && !rows.length} rows={shown} pageSize={15} columns={columns} emptyTitle="No matching entries" />
      </Card>
      {state.more && (
        <div className="row-actions">
          <Button variant="secondary" loading={state.loading} onClick={() => load(rows.length)}>Load older entries</Button>
        </div>
      )}
    </div>
  );
}
